import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from 'ahooks'
import { Alert, Form, Select } from 'antd'
import type { BillCreateInput } from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'

export function BillSourcePicker({
  onClose,
  onSelect
}: {
  onClose: () => void
  onSelect: (source: BillCreateInput['source']) => void
}) {
  const trpc = useTRPC()
  const [open, setOpen] = useState(true)
  const [selectedSource, setSelectedSource] = useState<BillCreateInput['source']>()
  const [form] = Form.useForm<BillCreateInput['source']>()
  const type = Form.useWatch<'rental' | 'service' | undefined>('type', form) ?? 'rental'
  const [keyword, setKeyword] = useState('')
  const debouncedKeyword = useDebounce(keyword, { wait: 300 })
  // 归档不关闭后续账务，来源选择仍可检索历史订单。
  const rentals = useQuery({
    ...trpc.order.rental.list.queryOptions({
      keyword: debouncedKeyword,
      current: 1,
      pageSize: 20,
      archiveStatus: 'all'
    }),
    enabled: type === 'rental'
  })
  const services = useQuery({
    ...trpc.order.service.list.queryOptions({
      keyword: debouncedKeyword,
      current: 1,
      pageSize: 20,
      archiveStatus: 'all'
    }),
    enabled: type === 'service'
  })
  const query = type === 'rental' ? rentals : services
  const options = (query.data?.data ?? []).map((order) => ({
    value: order.id,
    label: `${order.orderNo} · ${order.customerName}`,
    disabled:
      type === 'rental' ? order.status !== 'awaitingBilling' && order.status !== 'active' : order.status === 'cancelled'
  }))
  return (
    <ModalForm<BillCreateInput['source']>
      open={open}
      onOpenChange={setOpen}
      form={form}
      layout="vertical"
      title="选择账单来源"
      initialValues={{ type: 'rental' }}
      modalProps={{
        afterClose: () => (selectedSource ? onSelect(selectedSource) : onClose()),
        destroyOnHidden: true
      }}
      submitter={{ searchConfig: { submitText: '填写账单' } }}
      onFinish={async (values) => {
        setSelectedSource(values)
        setOpen(false)
        return true
      }}
    >
      {query.isError && <Alert type="error" title={query.error.message} />}
      <Form.Item name="type" label="订单类型" rules={[{ required: true }]}>
        <Select
          onChange={() => {
            form.setFieldValue('orderId', undefined)
            setKeyword('')
          }}
          options={[
            { value: 'rental', label: '租赁订单' },
            { value: 'service', label: '服务订单' }
          ]}
        />
      </Form.Item>
      <Form.Item
        name="orderId"
        label="来源订单"
        rules={[{ required: true, message: '请选择来源订单' }]}
        extra="输入订单号或客户名称搜索。租赁订单须先保存合同；已取消服务订单不可出账。"
      >
        <Select
          options={options}
          loading={query.isFetching}
          showSearch={{ filterOption: false, onSearch: setKeyword }}
        />
      </Form.Item>
    </ModalForm>
  )
}
